import { Fragment, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Route, Routes, useLocation, useParams, type Params } from 'react-router';

import { EditGuard } from '@/app/EditGuard';
import { Providers } from '@/app/providers';
import { ToSection } from '@/app/ToSection';
import { LegacyFrame } from '@/components/shell/LegacyFrame';
import { PASS_ROUTE, VISITOR_ROUTE } from '@/features/agent/team/visitorRoutes';
import { FEATURE_CAMPAIGNS } from '@/lib/features';
import { Navigate as KeepingNavigate } from '@/lib/navigation';

const lazyNamed = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((module) => ({ default: module[name] })));

const CategoriesPage = lazyNamed(() => import('@/features/categories/pages/CategoriesPage'), 'CategoriesPage');
const AccountPage = lazyNamed(() => import('@/features/account/pages/AccountPage'), 'AccountPage');
const ListeningSettingsPage = lazyNamed(() => import('@/features/listen/pages/ListeningSettingsPage'), 'ListeningSettingsPage');
const ListeningVoicesPage = lazyNamed(() => import('@/features/listen/pages/ListeningVoicesPage'), 'ListeningVoicesPage');
const ListeningHouseStylePage = lazyNamed(() => import('@/features/listen/pages/ListeningHouseStylePage'), 'ListeningHouseStylePage');
const ListeningPronunciationPage = lazyNamed(() => import('@/features/listen/pages/ListeningPronunciationPage'), 'ListeningPronunciationPage');
const ListeningStoragePage = lazyNamed(() => import('@/features/listen/pages/ListeningStoragePage'), 'ListeningStoragePage');
const ListeningKeysPage = lazyNamed(() => import('@/features/listen/pages/ListeningKeysPage'), 'ListeningKeysPage');
const ElementsSettingsPage = lazyNamed(() => import('@/features/plates/pages/ElementsSettingsPage'), 'ElementsSettingsPage');
const AuthCallback = lazyNamed(() => import('@/pages/AuthCallback'), 'AuthCallback');
const AuthenticatedShell = lazy(() => import('@/components/AuthenticatedShell'));
const CampaignGraphDemo = lazyNamed(() => import('@/features/campaigns/pages/CampaignGraphDemo'), 'CampaignGraphDemo');
const CampaignNew = lazyNamed(() => import('@/features/campaigns/pages/CampaignNew'), 'CampaignNew');
const CampaignWorkspace = lazyNamed(() => import('@/features/campaigns/pages/CampaignWorkspace'), 'CampaignWorkspace');
const CampaignsIndex = lazyNamed(() => import('@/features/campaigns/pages/CampaignsIndex'), 'CampaignsIndex');
const ShelvesPage = lazyNamed(() => import('@/features/shelves/pages/ShelvesPage'), 'ShelvesPage');
const ContentsPage = lazyNamed(() => import('@/features/contents/pages/ContentsPage'), 'ContentsPage');
const DocumentPage = lazyNamed(() => import('@/features/docs/pages/DocumentPage'), 'DocumentPage');
const EntryPage = lazyNamed(() => import('@/features/docs/pages/EntryPage'), 'EntryPage');
const SectionRedirect = lazyNamed(() => import('@/features/docs/pages/SectionRedirect'), 'SectionRedirect');
const KnowdexPage = lazyNamed(() => import('@/features/docs/pages/PlacePage'), 'KnowdexPage');
const IndexPage = lazyNamed(() => import('@/features/index/pages/IndexPage'), 'IndexPage');
const QuizzesPage = lazyNamed(() => import('@/features/docs/pages/PlacePage'), 'QuizzesPage');
const BriefsPage = lazyNamed(() => import('@/features/docs/pages/PlacePage'), 'BriefsPage');
const NewEntryPage = lazyNamed(() => import('@/features/newEntry/pages/NewEntryPage'), 'NewEntryPage');
const NewShelfPage = lazyNamed(() => import('@/features/shelf/pages/NewShelfPage'), 'NewShelfPage');
const TeamPage = lazyNamed(() => import('@/features/agent/team/TeamPages'), 'TeamPage');
const TeamJobPage = lazyNamed(() => import('@/features/agent/team/TeamPages'), 'TeamJobPage');
const TeamReviewPage = lazyNamed(() => import('@/features/agent/team/TeamPages'), 'TeamReviewPage');
const VisitorPage = lazyNamed(() => import('@/features/agent/team/VisitorPage'), 'VisitorPage');
const PassPage = lazyNamed(() => import('@/features/agent/team/PassPage'), 'PassPage');
const KPage = lazyNamed(() => import('@/features/agent/pages/KPage'), 'KPage');
const BriefLink = lazyNamed(() => import('@/features/agent/pages/BriefLink'), 'BriefLink');
const NotFound = lazyNamed(() => import('@/pages/NotFound'), 'NotFound');
const OpSetWorkspace = lazyNamed(() => import('@/features/operationSets/pages/OpSetWorkspace'), 'OpSetWorkspace');
const OperationSetsIndex = lazyNamed(() => import('@/features/operationSets/pages/OperationSetsIndex'), 'OperationSetsIndex');
const OperationWorkspace = lazyNamed(() => import('@/features/operations/pages/OperationWorkspace'), 'OperationWorkspace');
const OperationsIndex = lazyNamed(() => import('@/features/operations/pages/OperationsIndex'), 'OperationsIndex');
const PublicDocView = lazyNamed(() => import('@/features/share/pages/PublicDocView'), 'PublicDocView');
const QuizTake = lazyNamed(() => import('@/features/quizzes/pages/QuizTake'), 'QuizTake');
const QuizWorkspace = lazyNamed(() => import('@/features/quizzes/pages/QuizWorkspace'), 'QuizWorkspace');
const ShelfPage = lazyNamed(() => import('@/features/shelf/pages/ShelfPage'), 'ShelfPage');

/**
 * The app's data router: one splat route holds the providers and the route
 * table, so `useBlocker` works and every route keeps its declarative shape.
 */
export const createAppRouter = () =>
  createBrowserRouter([{ path: '*', element: <Providers><AppRoutes /><EditGuard /></Providers> }]);

/*
 * Index Book routes (DESIGN-SPEC Appendix A). Screens the redesign has not
 * reached yet render their existing page inside LegacyFrame; the old URLs
 * redirect to the new ones, keeping query and fragment.
 */
export function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/share/:token"
          element={<RouteScope paramNames={['token']}><PublicDocView /></RouteScope>}
        />
        <Route element={<AuthenticatedShell />}>
          <Route path="/" element={<KnowdexPage />} />
          <Route path="/index" element={<IndexPage />} />
          <Route path="/contents" element={<ContentsPage />} />
          <Route path="/shelves" element={<ShelvesPage />} />
          <Route path="/d/:docId" element={<RouteScope paramNames={['docId']}><DocumentPage /></RouteScope>} />
          <Route path="/d/:docId/s/:sectionId" element={<RouteScope paramNames={['docId']}><EntryPage /></RouteScope>} />
          <Route path="/s/:sectionId" element={<RouteScope paramNames={['sectionId']}><SectionRedirect /></RouteScope>} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/jobs/:id" element={<RouteScope><TeamJobPage /></RouteScope>} />
          <Route path="/team/review/:documentId" element={<RouteScope paramNames={['documentId']}><TeamReviewPage /></RouteScope>} />
          <Route path={VISITOR_ROUTE} element={<VisitorPage />} />
          <Route path={PASS_ROUTE} element={<PassPage />} />
          <Route path="/k" element={<KPage />} />
          <Route path="/briefs" element={<BriefsPage />} />
          <Route path="/briefs/:briefId" element={<RouteScope paramNames={['briefId']}><BriefLink /></RouteScope>} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/categories" element={<CategoriesPage />} />
          <Route path="/account/listening" element={<ListeningSettingsPage />} />
          <Route path="/account/listening/voices" element={<ListeningVoicesPage />} />
          <Route path="/account/listening/reading" element={<ListeningHouseStylePage />} />
          <Route path="/account/listening/pronunciation" element={<ListeningPronunciationPage />} />
          <Route path="/account/listening/storage" element={<ListeningStoragePage />} />
          <Route path="/account/listening/keys" element={<ListeningKeysPage />} />
          <Route path="/account/elements" element={<ElementsSettingsPage />} />
          <Route path="/shelf/new" element={<NewShelfPage />} />
          <Route path="/shelf/:shelfId" element={<RouteScope paramNames={['shelfId']}><ShelfPage /></RouteScope>} />
          <Route path="/new" element={<NewEntryPage />} />
          <Route path="/review" element={<QuizzesPage />} />
          <Route path="/review/:id" element={<RouteScope><QuizWorkspace /></RouteScope>} />
          <Route
            path="/review/:id/attempts/:attemptId/take"
            element={<RouteScope paramNames={['id', 'attemptId']}><QuizTake /></RouteScope>}
          />
          {!FEATURE_CAMPAIGNS && ['/campaigns/*', '/sets/*', '/operation-sets/*', '/operations/*'].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/" replace />} />
          ))}
          <Route element={<LegacyFrame />}>
            {FEATURE_CAMPAIGNS && (
              <>
                <Route path="/campaigns" element={<CampaignsIndex />} />
                <Route path="/campaigns/new" element={<CampaignNew />} />
                <Route path="/campaigns/demo" element={<CampaignGraphDemo />} />
                <Route path="/campaigns/:id" element={<RouteScope><CampaignWorkspace /></RouteScope>} />
                <Route path="/operation-sets" element={<OperationSetsIndex />} />
                <Route path="/sets/:id" element={<RouteScope><OpSetWorkspace /></RouteScope>} />
                <Route path="/operations" element={<OperationsIndex />} />
                <Route path="/operations/:id" element={<RouteScope><OperationWorkspace /></RouteScope>} />
              </>
            )}
          </Route>
          <Route path="/docs" element={<Redirect to={() => '/contents'} />} />
          <Route path="/docs/new" element={<Redirect to={() => '/new'} />} />
          <Route path="/docs/:id" element={<DocRedirect />} />
          <Route path="/quizzes" element={<Redirect to={() => '/review'} />} />
          <Route path="/quizzes/:id" element={<Redirect to={(p) => `/review/${p.id}`} />} />
          <Route path="/quizzes/:id/attempts/:attemptId/take" element={<Redirect to={(p) => `/review/${p.id}/attempts/${p.attemptId}/take`} />} />
          <Route path="/categories" element={<Redirect to={() => '/account/categories'} />} />
          <Route path="/settings/agent" element={<Redirect to={() => '/account#agent'} />} />
          <Route path="/settings/billing" element={<Redirect to={() => '/account#billing'} />} />
          <Route path="/settings" element={<Redirect to={() => '/account'} />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

/** Replaces an old URL with its Index Book equivalent, keeping the query and the fragment (a target's own fragment wins). */
function Redirect({ to }: { to: (params: Readonly<Params>) => string }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const [path, fragment] = to(params).split('#');
  return <KeepingNavigate to={`${path}${search}${fragment ? `#${fragment}` : hash}`} />;
}

/** `/docs/:id`, and `/docs/:id#section-<id>` to the section's own page (with the document behind it on a first entry). */
function DocRedirect() {
  const { id = '' } = useParams();
  const { search, hash } = useLocation();
  const section = hash.match(/^#section-(.+)$/);
  const params = new URLSearchParams(search);
  params.delete('tab');
  const query = params.toString() ? `?${params}` : '';
  if (section) return <ToSection docId={id} sectionId={section[1]!} rest={query} />;
  return <KeepingNavigate to={`/d/${id}${search}${hash}`} />;
}

function RouteScope({
  children,
  paramNames = ['id'],
}: {
  children: ReactNode;
  paramNames?: string[];
}) {
  const params = useParams();
  const scope = paramNames.map((name) => params[name] ?? '').join(':');
  return <Fragment key={scope}>{children}</Fragment>;
}

function RouteLoading() {
  return (
    <main className="route-loading">
      <p className="note" role="status">Loading…</p>
    </main>
  );
}
